import React, {
  PropsWithChildren,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import StatsPreview from "../interfaces/StatsPreview";
import EarliestDate from "../interfaces/EarliestDate";
import { BACKEND } from "../resources/constants/urls";
import {
  getPreviewMirror,
  isPreviewSyncing,
  subscribeToPreviewMirror,
  subscribeToPreviewSyncState,
} from "@appsinti/preview-mirror";
import { statsSummarySyncKey } from "./fetch";
import auth from "../resources/api/auth";
import { useDateProvider } from "@appsinti/date-provider";
import { format } from "date-fns";
import StatsDay from "../interfaces/StatsDay";
import IGroup from "@appsinti/common-utils/interfaces/IGroup";

const REQUEST_TIMEOUT = 20000;

export type DataContextType = {
  loadingPreview: (groupId: number | string) => boolean;
  loadingDay: (groupId: number | string) => boolean;
  loadingAnyDay: boolean;
  preview: { [key: number]: StatsPreview } | null;
  day: { [key: number]: StatsDay } | null;
  config: {
    appId: number;
  };
};

const DataContext = React.createContext<DataContextType>({
  loadingPreview: () => false,
  loadingDay: () => false,
  loadingAnyDay: false,
  preview: null,
  day: null,
  config: {
    appId: 0,
  },
});

export const usePreviewData = () => React.useContext(DataContext);

export const PreviewDataProvider: React.FC<
  PropsWithChildren<{
    appId: number;
    groups: { [key: number]: IGroup };
  }>
> = ({ appId, groups, children }) => {
  const [syncingGroups, setSyncingGroups] = useState<{
    [key: string]: boolean;
  }>({});
  const [settledGroups, setSettledGroups] = useState<{
    [key: string]: boolean;
  }>({});
  const [loadingDayGroups, setLoadingDayGroups] = useState<{
    [key: string]: boolean;
  }>({});
  const [preview, setPreview] = useState<{
    [key: number]: StatsPreview;
  } | null>(null);
  const { registerMinimumDate, currentDate } = useDateProvider();
  const [day, setDay] = useState<{ [key: number]: StatsDay } | null>(null);

  const groupIds = useMemo(() => Object.keys(groups), [groups]);
  const wasSyncing = useRef<{ [key: string]: boolean }>({});

  useEffect(() => {
    const initialSyncing: { [key: string]: boolean } = {};
    for (const groupId of groupIds) {
      initialSyncing[groupId] = isPreviewSyncing(
        statsSummarySyncKey(appId, groupId),
      );
    }
    wasSyncing.current = initialSyncing;
    setSyncingGroups(initialSyncing);
    setSettledGroups({});

    const readLocalPreview = () => {
      const mirror = getPreviewMirror(appId);
      if (!mirror) return;
      try {
        setPreview(JSON.parse(mirror) as { [key: number]: StatsPreview });
      } catch {
        return;
      }
    };

    const readSyncState = () => {
      const syncing: { [key: string]: boolean } = {};
      const settledNow: string[] = [];

      for (const groupId of groupIds) {
        const isSyncing = isPreviewSyncing(statsSummarySyncKey(appId, groupId));
        syncing[groupId] = isSyncing;
        // Settled only on this location's own transition out of syncing:
        // another location starting or finishing must not resolve this card.
        if (wasSyncing.current[groupId] && !isSyncing) settledNow.push(groupId);
      }

      wasSyncing.current = syncing;
      setSyncingGroups(syncing);

      if (settledNow.length === 0) return;
      setSettledGroups((prev) => {
        const next = { ...prev };
        for (const groupId of settledNow) next[groupId] = true;
        return next;
      });
    };

    readLocalPreview();

    const unsubscribeMirror = subscribeToPreviewMirror(readLocalPreview);
    const unsubscribeSyncState = subscribeToPreviewSyncState(readSyncState);

    return () => {
      unsubscribeMirror();
      unsubscribeSyncState();
    };
  }, [appId, groupIds]);

  const loadingPreview = useCallback(
    (groupId: number | string) =>
      !!syncingGroups[groupId] || !settledGroups[groupId],
    [syncingGroups, settledGroups],
  );

  const loadingDay = useCallback(
    (groupId: number | string) => !!loadingDayGroups[groupId],
    [loadingDayGroups],
  );

  const loadingAnyDay = useMemo(
    () => Object.values(loadingDayGroups).some((isLoading) => isLoading),
    [loadingDayGroups],
  );

  const registerDates = (dates: EarliestDate) => {
    const { minDate } = dates;
    if (minDate) registerMinimumDate(new Date(`${minDate}T04:00:00`));
  };

  const fetchEarliestDates = async () => {
    const { Authorization } = auth;
    for (const groupId in groups) {
      try {
        const dates = await axios.get<EarliestDate>(
          `${BACKEND}/appsinti/stats-summary/metadata/earliest-date`,
          {
            headers: {
              "app-id": appId,
              "group-id": groupId,
              Authorization: Authorization,
            },
          },
        );

        registerDates(dates.data);
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    fetchEarliestDates();
  }, [appId, groups]);

  const fetchGroupDate = async (
    date: Date,
    groupId: string,
    signal: AbortSignal,
  ): Promise<StatsDay | null> => {
    const { Authorization } = auth;
    try {
      const dateRes = await axios.get<StatsDay>(
        `${BACKEND}/appsinti/stats-summary/data/${format(date, "yyyy-MM-dd")}`,
        {
          headers: {
            "app-id": appId,
            "group-id": groupId,
            Authorization: Authorization,
          },
          signal,
          timeout: REQUEST_TIMEOUT,
        },
      );
      return dateRes.data;
    } catch (error) {
      if (!axios.isCancel(error)) console.error(error);
      return null;
    }
  };

  const fetchSpecificDate = async (date: Date, signal: AbortSignal) => {
    const pending: { [key: string]: boolean } = {};
    for (const groupId of groupIds) pending[groupId] = true;
    setLoadingDayGroups(pending);
    setDay({});

    await Promise.all(
      groupIds.map(async (groupId) => {
        const stats = await fetchGroupDate(date, groupId, signal);
        if (signal.aborted) return;

        if (stats) {
          setDay((prev) => ({ ...(prev ?? {}), [groupId]: stats }));
        }
        setLoadingDayGroups((prev) => ({ ...prev, [groupId]: false }));
      }),
    );
  };

  useEffect(() => {
    if (!currentDate) {
      setDay(null);
      setLoadingDayGroups({});
      return;
    }

    const controller = new AbortController();
    fetchSpecificDate(currentDate, controller.signal);

    return () => controller.abort();
  }, [currentDate, appId, groupIds]);

  return (
    <DataContext.Provider
      value={{
        loadingPreview,
        loadingDay,
        loadingAnyDay,
        preview,
        day,
        config: { appId },
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
